import { EventEmitter } from 'events';
import { z, ZodSchema } from 'zod';
import { logger } from '../utils/logger';

// 服务状态
export enum ServiceStatus {
  STARTING = 'starting',
  RUNNING = 'running',
  STOPPING = 'stopping',
  STOPPED = 'stopped',
  ERROR = 'error'
}

// 服务健康状态
export enum HealthStatus {
  HEALTHY = 'healthy',
  UNHEALTHY = 'unhealthy',
  DEGRADED = 'degraded',
  UNKNOWN = 'unknown'
}

// 服务接口
export interface IService {
  name: string;
  version: string;
  description?: string;
  dependencies?: string[];
  
  // 生命周期方法
  start(): Promise<void>;
  stop(): Promise<void>;
  
  // 健康检查
  healthCheck?(): Promise<HealthCheckResult>;
  
  // 获取服务信息
  getInfo?(): ServiceInfo;
}

// 服务信息
export interface ServiceInfo {
  name: string;
  version: string;
  description?: string;
  status: ServiceStatus;
  health: HealthStatus;
  uptime: number;
  metadata?: Record<string, any>;
}

// 健康检查结果
export interface HealthCheckResult {
  status: HealthStatus;
  message?: string;
  details?: Record<string, any>;
  timestamp: number;
}

// 服务注册选项
export interface ServiceRegistrationOptions {
  autoStart?: boolean;
  healthCheckInterval?: number;
  restartOnFailure?: boolean;
  maxRestartAttempts?: number;
  metadata?: Record<string, any>;
}

// 服务实例
class ServiceInstance {
  public status: ServiceStatus = ServiceStatus.STOPPED;
  public health: HealthStatus = HealthStatus.UNKNOWN;
  public startTime?: number;
  public lastHealthCheck?: HealthCheckResult;
  public restartAttempts = 0;
  public metadata: Record<string, any> = {};
  
  constructor(
    public service: IService,
    public options: ServiceRegistrationOptions = {}
  ) {
    this.metadata = options.metadata || {};
  }
  
  get uptime(): number {
    return this.startTime ? Date.now() - this.startTime : 0;
  }
  
  async start(): Promise<void> {
    if (this.status === ServiceStatus.RUNNING) {
      return;
    }
    
    this.status = ServiceStatus.STARTING;
    
    try {
      await this.service.start();
      this.status = ServiceStatus.RUNNING;
      this.startTime = Date.now();
      this.restartAttempts = 0;
    } catch (error) {
      this.status = ServiceStatus.ERROR;
      throw error;
    }
  }
  
  async stop(): Promise<void> {
    if (this.status === ServiceStatus.STOPPED) {
      return;
    }
    
    this.status = ServiceStatus.STOPPING;
    
    try {
      await this.service.stop();
      this.status = ServiceStatus.STOPPED;
      this.startTime = undefined;
    } catch (error) {
      this.status = ServiceStatus.ERROR;
      throw error;
    }
  }
  
  async checkHealth(): Promise<HealthCheckResult> {
    if (this.status !== ServiceStatus.RUNNING) {
      return {
        status: HealthStatus.UNHEALTHY,
        message: `Service is ${this.status}`,
        timestamp: Date.now()
      };
    }
    
    if (!this.service.healthCheck) {
      return {
        status: HealthStatus.HEALTHY,
        message: 'No health check defined',
        timestamp: Date.now()
      };
    }
    
    try {
      const result = await this.service.healthCheck();
      this.lastHealthCheck = result;
      this.health = result.status;
      return result;
    } catch (error) {
      const result: HealthCheckResult = {
        status: HealthStatus.UNHEALTHY,
        message: `Health check failed: ${error.message}`,
        timestamp: Date.now()
      };
      this.lastHealthCheck = result;
      this.health = result.status;
      return result;
    }
  }
  
  getInfo(): ServiceInfo {
    const baseInfo: ServiceInfo = {
      name: this.service.name,
      version: this.service.version,
      description: this.service.description,
      status: this.status,
      health: this.health,
      uptime: this.uptime,
      metadata: this.metadata
    };
    
    if (this.service.getInfo) {
      return { ...baseInfo, ...this.service.getInfo() };
    }
    
    return baseInfo;
  }
}

// 服务注册表
export class ServiceRegistry extends EventEmitter {
  private services = new Map<string, ServiceInstance>();
  private healthCheckIntervals = new Map<string, NodeJS.Timer>();
  private dependencyGraph = new Map<string, Set<string>>();
  
  constructor(
    private options: {
      defaultHealthCheckInterval?: number;
      enableAutoStart?: boolean;
    } = {}
  ) {
    super();
  }
  
  /**
   * 注册服务
   */
  async register(
    service: IService,
    options: ServiceRegistrationOptions = {}
  ): Promise<void> {
    const { name } = service;
    
    if (this.services.has(name)) {
      throw new Error(`Service "${name}" is already registered`);
    }
    
    // 验证依赖
    if (service.dependencies) {
      for (const dep of service.dependencies) {
        if (!this.services.has(dep)) {
          throw new Error(`Service "${name}" depends on unregistered service "${dep}"`);
        }
      }
    }
    
    // 创建服务实例
    const instance = new ServiceInstance(service, options);
    this.services.set(name, instance);
    
    // 更新依赖图
    if (service.dependencies) {
      this.dependencyGraph.set(name, new Set(service.dependencies));
    }
    
    // 自动启动
    if (options.autoStart ?? this.options.enableAutoStart) {
      await this.start(name);
    }
    
    // 设置健康检查
    if (service.healthCheck) {
      const interval = options.healthCheckInterval || 
                      this.options.defaultHealthCheckInterval || 
                      30000; // 30秒
      
      const timer = setInterval(async () => {
        await instance.checkHealth();
        this.emit('healthCheck', name, instance.lastHealthCheck);
      }, interval);
      
      this.healthCheckIntervals.set(name, timer);
    }
    
    this.emit('registered', name, instance.getInfo());
    logger.info(`Service "${name}" registered`);
  }
  
  /**
   * 注销服务
   */
  async unregister(name: string): Promise<void> {
    const instance = this.services.get(name);
    if (!instance) {
      throw new Error(`Service "${name}" not found`);
    }
    
    // 检查是否有其他服务依赖此服务
    const dependents = this.getDependents(name);
    if (dependents.length > 0) {
      throw new Error(
        `Cannot unregister service "${name}": depended by ${dependents.join(', ')}`
      );
    }
    
    // 停止服务
    if (instance.status === ServiceStatus.RUNNING) {
      await this.stop(name);
    }
    
    // 清理健康检查
    const timer = this.healthCheckIntervals.get(name);
    if (timer) {
      clearInterval(timer);
      this.healthCheckIntervals.delete(name);
    }
    
    // 移除服务
    this.services.delete(name);
    this.dependencyGraph.delete(name);
    
    this.emit('unregistered', name);
    logger.info(`Service "${name}" unregistered`);
  }
  
  /**
   * 启动服务
   */
  async start(name: string): Promise<void> {
    const instance = this.services.get(name);
    if (!instance) {
      throw new Error(`Service "${name}" not found`);
    }
    
    // 启动依赖服务
    const dependencies = instance.service.dependencies || [];
    for (const dep of dependencies) {
      const depInstance = this.services.get(dep);
      if (depInstance && depInstance.status !== ServiceStatus.RUNNING) {
        await this.start(dep);
      }
    }
    
    try {
      await instance.start();
      this.emit('started', name, instance.getInfo());
      logger.info(`Service "${name}" started`);
    } catch (error) {
      this.emit('startFailed', name, error);
      logger.error(`Failed to start service "${name}":`, error);
      
      // 尝试重启
      if (instance.options.restartOnFailure) {
        await this.handleServiceFailure(name, instance);
      } else {
        throw error;
      }
    }
  }
  
  /**
   * 停止服务
   */
  async stop(name: string): Promise<void> {
    const instance = this.services.get(name);
    if (!instance) {
      throw new Error(`Service "${name}" not found`);
    }
    
    // 停止依赖此服务的其他服务
    const dependents = this.getDependents(name);
    for (const dep of dependents) {
      const depInstance = this.services.get(dep);
      if (depInstance && depInstance.status === ServiceStatus.RUNNING) {
        await this.stop(dep);
      }
    }
    
    try {
      await instance.stop();
      this.emit('stopped', name, instance.getInfo());
      logger.info(`Service "${name}" stopped`);
    } catch (error) {
      this.emit('stopFailed', name, error);
      logger.error(`Failed to stop service "${name}":`, error);
      throw error;
    }
  }
  
  /**
   * 重启服务
   */
  async restart(name: string): Promise<void> {
    await this.stop(name);
    await this.start(name);
  }
  
  /**
   * 获取服务
   */
  get(name: string): IService | undefined {
    return this.services.get(name)?.service;
  }
  
  /**
   * 获取服务信息
   */
  getInfo(name: string): ServiceInfo | undefined {
    return this.services.get(name)?.getInfo();
  }
  
  /**
   * 获取所有服务
   */
  getAll(): Map<string, ServiceInfo> {
    const result = new Map<string, ServiceInfo>();
    
    for (const [name, instance] of this.services) {
      result.set(name, instance.getInfo());
    }
    
    return result;
  }
  
  /**
   * 检查服务健康状态
   */
  async checkHealth(name: string): Promise<HealthCheckResult> {
    const instance = this.services.get(name);
    if (!instance) {
      throw new Error(`Service "${name}" not found`);
    }
    
    return instance.checkHealth();
  }
  
  /**
   * 检查所有服务健康状态
   */
  async checkAllHealth(): Promise<Map<string, HealthCheckResult>> {
    const results = new Map<string, HealthCheckResult>();
    
    for (const [name, instance] of this.services) {
      results.set(name, await instance.checkHealth());
    }
    
    return results;
  }
  
  /**
   * 启动所有服务
   */
  async startAll(): Promise<void> {
    // 按依赖顺序启动
    const sorted = this.topologicalSort();
    
    for (const name of sorted) {
      const instance = this.services.get(name);
      if (instance && instance.status !== ServiceStatus.RUNNING) {
        await this.start(name);
      }
    }
  }
  
  /**
   * 停止所有服务
   */
  async stopAll(): Promise<void> {
    // 按依赖逆序停止
    const sorted = this.topologicalSort().reverse();
    
    for (const name of sorted) {
      const instance = this.services.get(name);
      if (instance && instance.status === ServiceStatus.RUNNING) {
        await this.stop(name);
      }
    }
  }
  
  /**
   * 清理资源
   */
  async dispose(): Promise<void> {
    // 停止所有服务
    await this.stopAll();
    
    // 清理健康检查定时器
    for (const timer of this.healthCheckIntervals.values()) {
      clearInterval(timer);
    }
    this.healthCheckIntervals.clear();
    
    // 清理数据
    this.services.clear();
    this.dependencyGraph.clear();
    
    // 移除所有监听器
    this.removeAllListeners();
  }
  
  // 私有方法
  
  /**
   * 获取依赖指定服务的服务列表
   */
  private getDependents(serviceName: string): string[] {
    const dependents: string[] = [];
    
    for (const [name, deps] of this.dependencyGraph) {
      if (deps.has(serviceName)) {
        dependents.push(name);
      }
    }
    
    return dependents;
  }
  
  /**
   * 拓扑排序（用于按依赖顺序启动服务）
   */
  private topologicalSort(): string[] {
    const visited = new Set<string>();
    const result: string[] = [];
    
    const visit = (name: string) => {
      if (visited.has(name)) return;
      visited.add(name);
      
      const deps = this.dependencyGraph.get(name);
      if (deps) {
        for (const dep of deps) {
          visit(dep);
        }
      }
      
      result.push(name);
    };
    
    for (const name of this.services.keys()) {
      visit(name);
    }
    
    return result;
  }
  
  /**
   * 处理服务失败
   */
  private async handleServiceFailure(
    name: string,
    instance: ServiceInstance
  ): Promise<void> {
    const maxAttempts = instance.options.maxRestartAttempts || 3;
    
    if (instance.restartAttempts >= maxAttempts) {
      const error = new Error(`Service "${name}" failed after ${maxAttempts} restart attempts`);
      logger.error(error.message);
      this.emit('serviceFailed', name);
      throw error;
    }
    
    instance.restartAttempts++;
    logger.info(`Attempting to restart service "${name}" (attempt ${instance.restartAttempts})`);
    
    // 延迟重启
    await new Promise(resolve => setTimeout(resolve, 1000 * instance.restartAttempts));
    
    try {
      await instance.start();
      logger.info(`Service "${name}" restarted successfully`);
      instance.restartAttempts = 0; // 重置重试次数
    } catch (error) {
      await this.handleServiceFailure(name, instance);
    }
  }
}

// 服务装饰器
export function Service(options: {
  name: string;
  version: string;
  description?: string;
  dependencies?: string[];
}) {
  return function <T extends new (...args: any[]) => any>(constructor: T) {
    return class extends constructor implements IService {
      name = options.name;
      version = options.version;
      description = options.description;
      dependencies = options.dependencies;
    };
  };
}